import React, { useEffect, useState } from 'react'
import {
  CAlert, CButton, CCard, CCardBody, CCardHeader, CCol, CForm, CFormInput,
  CFormLabel, CFormSelect, CInputGroup, CInputGroupText,
  CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle, CRow,
} from '@coreui/react'
import { NumericFormat } from 'react-number-format'
import { api } from '../../api/client'
import { capitalizarPalabras } from '../../utils/texto'
import { fmtMoneda, simboloMoneda } from '../../utils/moneda'
import { hoyLocal } from '../../utils/fecha'

/**
 * Modal compartido de edición de abono (grilla AbonosPage y detalle
 * AbonoViewPage) — réplica del modal de TARJETAS del webobject abono GX
 * Mode="UPD" (abonoww.cs:2896-2898 'DO UPDATE'; abono.cs:1908-1933 tipos de
 * abono, 2050-2053 préstamo llave deshabilitado).
 *
 * [RGM] 2026-10-03 (pedido Rafael): el modal BUENO es el de tarjetas/secciones
 * que estaba en la grilla; el detalle usa EL MISMO componente. Además, la
 * tarjeta "Fechas" va justo DEBAJO del "Préstamo Nro".
 */
const tiposAbono = ['Prestamo', 'Atraso', 'Condena', 'PrestamoCondena', 'PagoCapital', 'Interes', 'AbonoCapital']

interface ModificarAbonoModalProps {
  /** Fila del abono (abonoNro/abonoValor/abonoFecha/…); null = sin selección. */
  abono: any | null
  empresa: string
  visible: boolean
  onClose: () => void
  onSaved: () => void
}

interface FormEdicion {
  prestamoNro: string
  valor: string
  tipo: string
  cuotas: number
  fecha: string
  proximoCobro: string
  libreValor3: number
  libreCaracter1: string
  libreCaracter2: string
}

const formVacio = (): FormEdicion => ({
  prestamoNro: '', valor: '', tipo: 'Prestamo', cuotas: 0,
  fecha: hoyLocal(), proximoCobro: '',
  libreValor3: 0, libreCaracter1: '', libreCaracter2: '',
})

const ModificarAbonoModal: React.FC<ModificarAbonoModalProps> = ({
  abono, empresa, visible, onClose, onSaved,
}) => {
  const [form, setForm] = useState<FormEdicion>(formVacio)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')

  // Al abrir con una fila, precarga el form (abonoww UPD) con los valores
  // actuales del abono; el préstamo es llave y va solo lectura.
  useEffect(() => {
    if (!visible || !abono) return
    setForm({
      prestamoNro: String(abono.prestamoNro ?? ''),
      valor: String(abono.abonoValor ?? ''),
      tipo: String(abono.abonoTipoAbono ?? 'Prestamo'),
      cuotas: abono.abonoCuotas != null ? Number(abono.abonoCuotas) : 0,
      fecha: String(abono.abonoFecha ?? '').slice(0, 10) || hoyLocal(),
      proximoCobro: String(abono.abonoProximoCobro ?? '').slice(0, 10),
      // AbonoLibreValor3 = Método de Pago (1=Efectivo, 2=Transferencia —
      // semántica RGM); AbonoLibreCaracter1 = Observación del abono.
      libreValor3: Number(abono.abonoLibreValor3 ?? 0),
      libreCaracter1: String(abono.abonoLibreCaracter1 ?? ''),
      libreCaracter2: String(abono.abonoLibreCaracter2 ?? ''),
    })
    setError('')
    setGuardando(false)
  }, [visible, abono])

  // Guardar edición (abonoww.cs:2896-2898 — DO UPDATE: webobject abono
  // Mode="UPD"). Contrato del PUT /api/abonos/{nro} (AbonosController.Modificar):
  // fechas YYYY-MM-DD y campos opcionales en null (el backend no toca null).
  const guardar = async () => {
    if (!abono) return
    if (!form.valor || !form.fecha || !form.proximoCobro) {
      setError('Valor, Fecha y Próximo Cobro son obligatorios')
      return
    }
    // [RGM] 2026-10-14 (pedido Rafael): misma alerta del alta — si el AUMENTO
    // del valor supera el saldo pendiente del préstamo (el saldo mostrado ya
    // tiene aplicado el abono actual), avisar antes de guardar. No bloquea.
    const valorNuevo = Number(form.valor)
    const aumento = valorNuevo - Number(abono.abonoValor ?? 0)
    const saldoPendiente = Number(abono.prestamoSaldo ?? 0)
    if (saldoPendiente > 0 && aumento > saldoPendiente) {
      const continuar = window.confirm(
        `El abono ${fmtMoneda(valorNuevo)} supera el saldo pendiente (${fmtMoneda(saldoPendiente)}).\n\n¿Está seguro? Verifique el valor digitado.`,
      )
      if (!continuar) return
    }
    setGuardando(true)
    setError('')
    try {
      // Campos editables del form GX (abono.cs:1908-1933, 2050-2053): Valor,
      // Fecha (admin), Próximo cobro, Cuotas y libres — el tipo y el préstamo
      // no cambian (el tipo está deshabilitado en UPD, abono.cs:1912).
      await api.put(`/abonos/${abono.abonoNro}`, {
        empresaCodigo: empresa,
        abonoValor: Number(form.valor),
        abonoFecha: form.fecha,
        abonoProximoCobro: form.proximoCobro,
        abonoCuotas: Number(form.cuotas) > 0 ? Number(form.cuotas) : null,
        abonoLibreValor3: Number(form.libreValor3) > 0 ? Number(form.libreValor3) : null,
        abonoLibreCaracter1: form.libreCaracter1 || null,
        abonoLibreCaracter2: form.libreCaracter2 || null,
      })
      onSaved()
    } catch (e: any) {
      setError(e.message ?? 'No se pudo modificar el abono')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <CModal visible={visible} onClose={onClose}>
      <CModalHeader>
        <CModalTitle>Modificar abono</CModalTitle>
      </CModalHeader>
      <CModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {error && <CAlert color="danger">{error}</CAlert>}
        <CForm onSubmit={(e) => { e.preventDefault(); guardar() }}>
          {/* Tarjeta 1 — Cliente o préstamo: en UPD el préstamo es llave y se
              muestra fijo (abono.cs:2050-2053). */}
          <CCard className="mb-2">
            <CCardHeader className="bg-body-tertiary fw-semibold py-2">Cliente o préstamo</CCardHeader>
            <CCardBody className="pt-2">
              <CRow className="g-2">
                <CCol md={12}>
                  <CFormLabel>Préstamo Nro</CFormLabel>
                  <CFormInput value={form.prestamoNro} disabled />
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>

          {/* Tarjeta 2 — Fechas [RGM] 2026-10-03 (pedido Rafael): justo DEBAJO
              del número del préstamo. */}
          <CCard className="mb-2">
            <CCardHeader className="bg-body-tertiary fw-semibold py-2">Fechas</CCardHeader>
            <CCardBody className="pt-2">
              <CRow className="g-2">
                <CCol md={6}>
                  <CFormLabel>Fecha</CFormLabel>
                  <CFormInput type="date" value={form.fecha}
                    onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
                  {/* [RGM] 2026-10-03 (pedido Rafael): aviso al editar con una
                      fecha distinta a hoy — altera documentos anteriores (el
                      GX no trae este aviso; es diseño nuevo aprobado). */}
                  {form.fecha && form.fecha !== hoyLocal() && (
                    <small className="text-warning d-block mt-1">
                      Al cambiar la fecha se alterarán documentos anteriores.
                    </small>
                  )}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Próximo cobro</CFormLabel>
                  <CFormInput type="date" value={form.proximoCobro}
                    onChange={(e) => setForm({ ...form, proximoCobro: e.target.value })} />
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>

          {/* Tarjeta 3 — Abono: campos editables del form GX. */}
          <CCard className="mb-2">
            <CCardHeader className="bg-body-tertiary fw-semibold py-2">Abono</CCardHeader>
            <CCardBody className="pt-2">
              <CRow className="g-2">
                <CCol md={6}>
                  <CFormLabel>Valor</CFormLabel>
                  {/* Moneda global: prefijo de la moneda configurada + formato
                      de miles en vivo (utils/moneda.ts). */}
                  <CInputGroup>
                    <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                    <NumericFormat
                      className="form-control"
                      value={form.valor}
                      onValueChange={(v) => setForm({ ...form, valor: v.value })}
                      thousandSeparator=","
                      decimalSeparator="."
                      decimalScale={2}
                      fixedDecimalScale
                      allowNegative={false}
                      inputMode="decimal"
                    />
                  </CInputGroup>
                </CCol>
                <CCol md={6}>
                  {/* Tipo del abono: NO cambia en UPD (deshabilitado,
                      abono.cs:1912). */}
                  <CFormLabel>Tipo</CFormLabel>
                  <CFormSelect value={form.tipo} disabled
                    onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                    {tiposAbono.map((t) => <option key={t} value={t}>{t}</option>)}
                  </CFormSelect>
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Cuotas</CFormLabel>
                  <CFormInput type="number" value={form.cuotas}
                    onChange={(e) => setForm({ ...form, cuotas: Number(e.target.value) })} />
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>

          {/* Tarjeta 4 — Método de Pago: AbonoLibreValor3 (1/2) y
              AbonoLibreCaracter1 (Observación). */}
          <CCard className="mb-2">
            <CCardHeader className="bg-body-tertiary fw-semibold py-2">Método de Pago</CCardHeader>
            <CCardBody className="pt-2">
              <CRow className="g-2">
                <CCol md={6}>
                  <CFormLabel>Método</CFormLabel>
                  <CFormSelect value={String(form.libreValor3)}
                    onChange={(e) => setForm({ ...form, libreValor3: Number(e.target.value) })}>
                    <option value="1">Efectivo</option>
                    <option value="2">Transferencia</option>
                  </CFormSelect>
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Observación</CFormLabel>
                  <CFormInput value={form.libreCaracter1}
                    onChange={(e) => setForm({ ...form, libreCaracter1: capitalizarPalabras(e.target.value) })} />
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CForm>
      </CModalBody>
      <CModalFooter className="border-top">
        <CButton color="secondary" type="button" onClick={onClose}>Cancelar</CButton>
        <CButton color="primary" type="button" disabled={guardando} onClick={guardar}>
          {guardando ? 'Guardando…' : 'Guardar cambios'}
        </CButton>
      </CModalFooter>
    </CModal>
  )
}

export default ModificarAbonoModal
